/**
 * ActionPanel Component
 *
 * Displays a panel of action buttons for player to choose from.
 * Handles layout, submission, and real-time updates.
 * 
 * REFACTORED: Now accepts props (middle/low component pattern).
 * Parent (PhoneView) passes panel data and handlers.
 */

import React, { useState } from "react";
import { ActionPanelDefinition } from "@/types/actions"
import { HapticFeedbackService } from "@/services/hapticFeedback"
import { useQuestionTimer } from "@/hooks/useQuestionTimer"
import { ActionButton } from "../ActionButton"
import { Card } from "@/components/ui/card"

interface ActionPanelProps {
  panel: ActionPanelDefinition;
  requestId: string;
  onActionClick: (actionId: string) => void;
  disabled?: boolean;
  /**
   * Action IDs found in the phone info HTML (data-action-id).
   * In display_mode="html_bridge" those actions are already clickable in the
   * HTML, so the panel does not render duplicate buttons for them.
   */
  bridgedActionIds?: ReadonlySet<string>;
}

/**
 * ActionPanel - Displays action buttons for player input.
 *
 * Receives panel definition and handlers from parent.
 * Handles layout, timer countdown, and click events.
 */
export const ActionPanel: React.FC<ActionPanelProps> = ({
  panel,
  requestId,
  onActionClick,
  disabled = false,
  bridgedActionIds,
}) => {
  const [loading, setLoading] = useState(false)
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const { timeLeft } = useQuestionTimer(panel.timeout_seconds, requestId)


  const handleClick = async (actionId: string) => {
    if (disabled || loading) return

    setLoading(true)
    setSelectedAction(actionId)
    HapticFeedbackService.notification()

    try {
      await onActionClick(actionId)
      HapticFeedbackService.success()
    } catch (err) {
      console.error("[ActionPanel] Error in action click:", err)
      HapticFeedbackService.error()
      setSelectedAction(null)
    } finally {
      setLoading(false)
    }
  }

  const visibleActions =
    panel.display_mode === "html_bridge" && bridgedActionIds
      ? panel.actions.filter((action) => !bridgedActionIds.has(action.id))
      : panel.actions

  const getLayoutClasses = () => {
    switch (panel.layout) {
      case "horizontal":
        return "flex flex-row flex-wrap gap-3"
      case "grid": {
        // Dynamic grid columns based on action count
        const actionCount = visibleActions.length
        const cols = actionCount <= 2 ? 2 : actionCount <= 4 ? 3 : 4
        return `grid grid-cols-${cols} gap-3`
      }
      case "vertical":
      default:
        return "flex flex-col gap-3"
    }
  }

  return (
    <Card
      className="border-t-2 border-primary overflow-hidden flex flex-col min-h-0"
      data-testid="action-panel"
      data-request-id={requestId}
    >
      {/* The card itself must not scroll — its border and rounded corners have to
          stay in view; everything inside it scrolls as one area instead. */}
      <div className="p-6 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 flex justify-between items-end">
          <div>
            {panel.title && (
              <h2 className="text-sm font-semibold text-primary mb-1">
                {panel.title}
              </h2>
            )}
            {panel.description && (
              <p className="text-muted-foreground text-xs leading-relaxed max-w-[80%]">
                {panel.description}
              </p>
            )}
          </div>

          {/* Status indicator with optional timer */}
           {timeLeft !== null && timeLeft >= 0 && (
            <div className="flex items-center gap-2 px-2 py-1 bg-muted rounded border border-border" data-testid="action-timer">
              <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-ping" />
              <span className="text-[8px] text-muted-foreground font-bold">
                {timeLeft}s
              </span>
            </div>
          )}
        </div>

        {/* Actions Grid/List */}
        <div className={getLayoutClasses()}>
          {visibleActions.map((action) => (
            <ActionButton
              key={action.id}
              action={action}
              onClick={() => handleClick(action.id)}
              loading={loading && selectedAction === action.id}
              disabled={disabled || loading}
            />
          ))}
        </div>
      </div>
    </Card>
  )
}
